<template>
    <div>
        <!-- Modèle (commun texte et fichier) -->
        <div v-if="hasModelOptions" class="form-group mb-2 mt-2 d-flex align-items-center gap-2">
            <label for="select-model-file" class="form-label mb-0 text-nowrap pr-1" style="font-size: 14px;">Traduction</label>
            <select id="select-model-file" class="form-control" :value="selectedModel"
                :disabled="isLoading || isDisabled" @change="onModelChange">
                <option v-for="m in modelOptions" :key="m.value" :value="m.value">{{ m.label }}</option>
            </select>
        </div>
        <div v-else-if="!isLoadingModels" class="o-alert o-alert--error mb-3" role="alert">
            <div class="o-alert__icon">
                <svg class="svg-icon svg-circle-cross" aria-hidden="true" focusable="false">
                    <use xlink:href="/wp-content/themes/ans/design-system/svg-icons/icon-sprite.svg#circle-cross"></use>
                </svg>
            </div>
            <h2 class="o-alert__title">{{ config.input.messages.noModelAlertTitle }}</h2>
            <p>{{ config.input.messages.noModelAlertBody }}</p>
        </div>

        <!-- Colonnes de sortie -->
        <p class="fw-semibold mb-1 text-muted">Options de traduction</p>
        <div class="d-flex flex-wrap gap-3">
            <div v-for="col in config.fileInput.outputColumns" :key="col.key" class="custom-control custom-checkbox">
                <input type="checkbox" class="custom-control-input" :id="col.id" :checked="outputColumns[col.key]"
                    :disabled="isLoading" @change="update(col.key, $event)">
                <label class="custom-control-label" :for="col.id">{{ col.label }}</label>
            </div>
        </div>
    </div>
</template>

<script>
import config from '../config/translator.config.js';

/**
 * Composant de paramètres pour la traduction par fichier Excel.
 * La liste des colonnes est pilotée par translator.config.js.
 *
 * @prop  {Boolean} isLoading      - Désactive les contrôles pendant une traduction.
 * @prop  {String}  selectedModel  - Identifiant du modèle sélectionné (.sync).
 * @prop  {Array}   modelOptions   - Liste des modèles disponibles { value, label }.
 * @prop  {Object}  outputColumns  - Colonnes activées (.sync).
 * @emits update:selectedModel     - Nouveau modèle sélectionné.
 * @emits update:outputColumns     - Nouvel objet outputColumns après changement d'une checkbox.
 */
export default {
    name: 'TranslatorFileParameterInput',
    props: {
        /** Désactive les contrôles pendant le chargement. */
        isLoading: {
            type: Boolean,
            default: false
        },
        /** Désactive le sélecteur de modèle quand l'utilisateur n'est pas connecté. */
        isDisabled: {
            type: Boolean,
            default: false
        },
        /** Modèle de traduction actuellement sélectionné. */
        selectedModel: {
            type: String,
            default: ''
        },
        /** Liste des modèles à afficher dans le select. */
        modelOptions: {
            type: Array,
            default: () => []
        },
        /** Vrai pendant le chargement initial des modèles, pour éviter l'affichage prématuré de l'erreur. */
        isLoadingModels: {
            type: Boolean,
            default: false
        },
        /** Colonnes de sortie sélectionnées par l'utilisateur. */
        outputColumns: {
            type: Object,
            default: () => Object.fromEntries(
                config.fileInput.outputColumns.map(col => [col.key, col.defaultValue])
            )
        }
    },
    data() {
        return { config }
    },
    computed: {
        hasModelOptions() {
            return this.modelOptions.length > 0
        }
    },
    methods: {
        onModelChange(event) {
            this.$emit('update:selectedModel', event.target.value)
        },
        /**
         * Met à jour une colonne et émet le nouvel objet via 'update:outputColumns'.
         */
        update(key, event) {
            this.$emit('update:outputColumns', { ...this.outputColumns, [key]: event.target.checked })
        }
    }
}
</script>
